// Service Registry - Centralized service management
// ✅ NO MAGIC STRINGS: Use typed enums and constants

import { GlobalEvents } from "../constants/Events";
import { EventBus } from "../events/EventBus";
import debugLogger from "../utils/debugLogger";

import { ServiceLoader } from "./ServiceLoader";
import {
  ServiceConfig,
  ServiceMetadata,
  ServiceRegistryContract,
} from "./types";

export interface ServiceInitializationProgress {
  serviceName: string;
  status: "pending" | "loading" | "completed" | "failed";
  progress: number; // 0-100
  totalServices: number;
  currentIndex: number;
  error?: Error;
  timestamp: string;
}

export class ServiceRegistry implements ServiceRegistryContract {
  private services: Map<string, ServiceMetadata> = new Map();
  private configs: Map<string, ServiceConfig> = new Map();
  private loader: ServiceLoader;
  private initialized: boolean = false;
  private eventBus?: EventBus;
  private progressCallbacks: Set<
    (progress: ServiceInitializationProgress) => void
  > = new Set();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private preInstantiatedServices: Map<string, any> = new Map();

  constructor(eventBus?: EventBus) {
    this.loader = new ServiceLoader();
    this.eventBus = eventBus;

    // Register pre-instantiated services
    if (eventBus) {
      this.preInstantiatedServices.set("eventBus", eventBus);
    }
  }

  /**
   * Register a pre-instantiated service (e.g., eventBus that's already created)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerPreInstantiated(name: string, instance: any): void {
    this.preInstantiatedServices.set(name, instance);

    // If service is already registered in configs, mark it as initialized
    const metadata = this.services.get(name);
    if (metadata) {
      metadata.instance = instance;
      metadata.initialized = true;
    }
  }

  /**
   * Subscribe to service initialization progress
   */
  onProgress(
    callback: (progress: ServiceInitializationProgress) => void,
  ): () => void {
    this.progressCallbacks.add(callback);
    return () => {
      this.progressCallbacks.delete(callback);
    };
  }

  /**
   * Emit progress event
   */
  private emitProgress(progress: ServiceInitializationProgress): void {
    // Call all callbacks
    this.progressCallbacks.forEach((callback) => {
      try {
        callback(progress);
      } catch (error) {
        debugLogger.error("Error in progress callback:", error);
      }
    });

    // Emit global event if eventBus is available
    if (this.eventBus) {
      this.eventBus.emitGlobal(
        GlobalEvents.SERVICE_INITIALIZATION_PROGRESS,
        progress,
      );
    }
  }

  /**
   * Register a service configuration
   */
  register(config: ServiceConfig): void {
    if (!config.enabled) {
      return;
    }

    // If service is pre-instantiated, mark it as initialized
    const isPreInstantiated = this.preInstantiatedServices.has(config.name);

    this.configs.set(config.name, config);
    this.services.set(config.name, {
      name: config.name,
      type: config.type,
      instance: isPreInstantiated
        ? this.preInstantiatedServices.get(config.name)
        : undefined,
      initialized: isPreInstantiated,
      dependencies: config.dependencies,
      config: config.config,
    });
  }

  /**
   * Get a service instance by name
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get<T = any>(name: string): T | undefined {
    const metadata = this.services.get(name);
    if (!metadata || !metadata.initialized) {
      return undefined;
    }
    return metadata.instance as T;
  }

  /**
   * Check if a service is initialized
   */
  isInitialized(name: string): boolean {
    const metadata = this.services.get(name);
    return metadata?.initialized || false;
  }

  /**
   * Get service metadata
   */
  getServiceMetadata(name: string): ServiceMetadata | undefined {
    return this.services.get(name);
  }

  /**
   * Get all services metadata
   */
  getAllServices(): ServiceMetadata[] {
    return Array.from(this.services.values());
  }

  /**
   * Initialize all registered services in dependency order
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    // Topological sort based on dependencies
    const sortedServices = this.topologicalSort();
    const totalServices = sortedServices.length;

    // Initialize services in order
    for (let i = 0; i < sortedServices.length; i++) {
      const serviceName = sortedServices[i];
      const config = this.configs.get(serviceName);
      if (!config) {
        continue;
      }

      // Emit loading progress
      this.emitProgress({
        serviceName,
        status: "loading",
        progress: (i / totalServices) * 100,
        totalServices,
        currentIndex: i,
        timestamp: new Date().toISOString(),
      });

      try {
        // Check if service is pre-instantiated
        if (this.preInstantiatedServices.has(serviceName)) {
          const preInstantiated = this.preInstantiatedServices.get(serviceName);
          const metadata = this.services.get(serviceName)!;
          metadata.instance = preInstantiated;
          metadata.initialized = true;

          // Emit completed progress
          this.emitProgress({
            serviceName,
            status: "completed",
            progress: ((i + 1) / totalServices) * 100,
            totalServices,
            currentIndex: i,
            timestamp: new Date().toISOString(),
          });
          continue; // Skip loading, already initialized
        }

        // Get dependencies
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dependencies = new Map<string, any>();
        for (const depName of config.dependencies) {
          // Check pre-instantiated services first
          if (this.preInstantiatedServices.has(depName)) {
            dependencies.set(
              depName,
              this.preInstantiatedServices.get(depName),
            );
          } else {
            const depMetadata = this.services.get(depName);
            if (!depMetadata || !depMetadata.initialized) {
              throw new Error(
                `Service ${serviceName} dependency ${depName} not initialized`,
              );
            }
            dependencies.set(depName, depMetadata.instance);
          }
        }

        // Load and initialize service
        const instance = await this.loader.loadService(config, dependencies);

        // Validate interface contract if declared in config
        this.loader.validateContract(config, instance);

        // Update metadata
        const metadata = this.services.get(serviceName)!;
        metadata.instance = instance;
        metadata.initialized = true;

        // Emit completed progress
        this.emitProgress({
          serviceName,
          status: "completed",
          progress: ((i + 1) / totalServices) * 100,
          totalServices,
          currentIndex: i,
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        const metadata = this.services.get(serviceName)!;
        const errorObj =
          error instanceof Error ? error : new Error(String(error));
        metadata.error = errorObj;

        // Emit failed progress
        this.emitProgress({
          serviceName,
          status: "failed",
          progress: (i / totalServices) * 100,
          totalServices,
          currentIndex: i,
          error: errorObj,
          timestamp: new Date().toISOString(),
        });

        throw new Error(
          `Failed to initialize service ${serviceName}: ${errorObj.message}`,
        );
      }
    }

    this.initialized = true;

    // Emit final completion
    this.emitProgress({
      serviceName: "all",
      status: "completed",
      progress: 100,
      totalServices,
      currentIndex: totalServices - 1,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Get initialization status for all services
   */
  getInitializationStatus(): Array<{
    name: string;
    status: "pending" | "loading" | "completed" | "failed";
    dependencies: string[];
    error?: Error;
  }> {
    // If no services registered, return empty array
    if (this.configs.size === 0) {
      return [];
    }

    try {
      const sortedServices = this.topologicalSort();
      return sortedServices.map((serviceName) => {
        const metadata = this.services.get(serviceName);
        const config = this.configs.get(serviceName);

        let status: "pending" | "loading" | "completed" | "failed" = "pending";
        if (metadata?.initialized) {
          status = "completed";
        } else if (metadata?.error) {
          status = "failed";
        }

        return {
          name: serviceName,
          status,
          dependencies: config?.dependencies || [],
          error: metadata?.error,
        };
      });
    } catch {
      // If topological sort fails (e.g., circular dependency), return all configs
      return Array.from(this.configs.values()).map((config) => {
        const metadata = this.services.get(config.name);
        let status: "pending" | "loading" | "completed" | "failed" = "pending";
        if (metadata?.initialized) {
          status = "completed";
        } else if (metadata?.error) {
          status = "failed";
        }

        return {
          name: config.name,
          status,
          dependencies: config.dependencies || [],
          error: metadata?.error,
        };
      });
    }
  }

  /**
   * Topological sort of services based on dependencies
   */
  private topologicalSort(): string[] {
    const visited = new Set<string>();
    const visiting = new Set<string>();
    const result: string[] = [];

    const visit = (serviceName: string) => {
      if (visiting.has(serviceName)) {
        throw new Error(
          `Circular dependency detected involving service: ${serviceName}`,
        );
      }
      if (visited.has(serviceName)) {
        return;
      }

      visiting.add(serviceName);
      const config = this.configs.get(serviceName);
      if (config) {
        for (const dep of config.dependencies) {
          visit(dep);
        }
      }
      visiting.delete(serviceName);
      visited.add(serviceName);
      result.push(serviceName);
    };

    // Sort by priority first, then visit
    const sortedConfigs = Array.from(this.configs.values()).sort(
      (a, b) => a.priority - b.priority,
    );
    for (const config of sortedConfigs) {
      if (!visited.has(config.name)) {
        visit(config.name);
      }
    }

    return result;
  }
}
